import React from "react"
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from "recharts"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"
import { Button } from "../../components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs"
import { Progress } from "../../components/ui/progress"
import { Separator } from "../../components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination"

// 차트 색은 카탈로그 프레스 토큰(--ink 계열)을 렌더 시점에 읽는다.
// SVG fill 속성은 var()를 풀지 못하므로 computed style로 읽고,
// 다크 토글(<html class="dark">)에 MutationObserver로 반응한다.
const CHART_COLOR_FALLBACK = {
  mono: "#17130E",
  soft: "#6E6559",
  light: "#A79A87",
  grid: "rgba(110,101,89,0.2)",
  cursor: "rgba(110,101,89,0.06)",
  tooltipBorder: "rgba(110,101,89,0.28)",
  tooltipBg: "#FFFEFA",
  tooltipText: "#17130E",
}

function readChartColors() {
  if (typeof window === "undefined") return CHART_COLOR_FALLBACK
  const styles = window.getComputedStyle(document.documentElement)
  const token = (name, fallback) => styles.getPropertyValue(name).trim() || fallback
  const dim = token("--ink-dim", CHART_COLOR_FALLBACK.soft)
  return {
    mono: token("--ink", CHART_COLOR_FALLBACK.mono),
    soft: dim,
    light: token("--ink-faint", CHART_COLOR_FALLBACK.light),
    grid: `color-mix(in oklab, ${dim} 26%, transparent)`,
    cursor: `color-mix(in oklab, ${dim} 8%, transparent)`,
    tooltipBorder: `color-mix(in oklab, ${dim} 34%, transparent)`,
    tooltipBg: token("--sheet", CHART_COLOR_FALLBACK.tooltipBg),
    tooltipText: token("--ink", CHART_COLOR_FALLBACK.tooltipText),
  }
}

function useChartColors() {
  const [colors, setColors] = React.useState(readChartColors)
  React.useEffect(() => {
    const update = () => setColors(readChartColors())
    update()
    const observer = new MutationObserver(update)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])
  return colors
}

const META_DIMENSIONS = [
  { key: "origin", label: "Origin" },
  { key: "roast_level", label: "Roast" },
]
const SECTION_PAGE_SIZE = 8

const POINT_BUTTON_CLASS =
  "bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-ring/40"

function tx(i18n, key, fallback = "") {
  const value = i18n?.[key]
  if (typeof value !== "string" || value.length === 0) return fallback
  return value
}

function fmt(template, replacements = {}) {
  return Object.entries(replacements).reduce((acc, [key, value]) => {
    return acc.replaceAll(`%{${key}}`, String(value))
  }, String(template || ""))
}

function normalizeLocale(locale) {
  const normalized = String(locale || "ko").replace("_", "-")
  return normalized.length > 0 ? normalized : "ko"
}

function formatInteger(value, locale) {
  const number = Number(value)
  if (!Number.isFinite(number)) return "0"
  return new Intl.NumberFormat(normalizeLocale(locale), { maximumFractionDigits: 0 }).format(number)
}

function StatCard({ label, value, hint }) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardDescription className="font-mono text-[11px] uppercase tracking-[0.16em]">{label}</CardDescription>
        <CardTitle className="text-2xl sm:text-3xl">{value}</CardTitle>
        {hint ? <CardDescription>{hint}</CardDescription> : null}
      </CardHeader>
    </Card>
  )
}

function EmptyState({ text }) {
  return <p className="text-sm text-muted-foreground">{text}</p>
}

function TodayActionCard({
  hasCoffees,
  hasEvaluations,
  links,
  onGoSnapshot,
  onGoMemos,
  i18n,
}) {
  if (!hasCoffees) {
    return (
      <Card className="border-zinc-900/15">
        <CardHeader className="pb-3">
          <CardDescription className="font-mono text-[11px] uppercase tracking-[0.16em]">{tx(i18n, "today_action_label", "Today Action")}</CardDescription>
          <CardTitle>{tx(i18n, "today_action_no_coffee_title", "첫 원두를 등록해 운영 흐름을 시작하세요")}</CardTitle>
          <CardDescription>{tx(i18n, "today_action_no_coffee_body", "URL 또는 이미지 파싱으로 원두 아카이브를 1개만 먼저 만들면 이후 단계가 빨라집니다.")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild className={POINT_BUTTON_CLASS}>
            <a href={links.new_coffee_path || "#"}>{tx(i18n, "today_action_no_coffee_primary", "원두 등록")}</a>
          </Button>
          <Button variant="outline" asChild>
            <a href={links.coffees_path || "#"}>{tx(i18n, "today_action_no_coffee_secondary", "원두 목록")}</a>
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!hasEvaluations) {
    return (
      <Card className="border-zinc-900/15">
        <CardHeader className="pb-3">
          <CardDescription className="font-mono text-[11px] uppercase tracking-[0.16em]">{tx(i18n, "today_action_label", "Today Action")}</CardDescription>
          <CardTitle>{tx(i18n, "today_action_no_eval_title", "첫 게스트 피드백을 수집해 보세요")}</CardTitle>
          <CardDescription>{tx(i18n, "today_action_no_eval_body", "원두 상세 화면에서 QR 세션을 열고 첫 평가를 받으면 인사이트 카드가 활성화됩니다.")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild className={POINT_BUTTON_CLASS}>
            <a href={links.coffees_path || "#"}>{tx(i18n, "today_action_no_eval_primary", "QR 세션 열기")}</a>
          </Button>
          <Button variant="outline" onClick={onGoSnapshot}>
            {tx(i18n, "today_action_no_eval_secondary", "Coffee Snapshot 보기")}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-zinc-900/15">
      <CardHeader className="pb-3">
        <CardDescription className="font-mono text-[11px] uppercase tracking-[0.16em]">{tx(i18n, "today_action_label", "Today Action")}</CardDescription>
        <CardTitle>{tx(i18n, "today_action_default_title", "최근 메모를 점검하고 다음 실험을 정하세요")}</CardTitle>
        <CardDescription>{tx(i18n, "today_action_default_body", "긍정 메모를 기준으로 브루 레시피를 하나 정해 실험하면 개선 속도가 빨라집니다.")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button className={POINT_BUTTON_CLASS} onClick={onGoSnapshot}>
          {tx(i18n, "today_action_default_primary", "Coffee Snapshot 보기")}
        </Button>
        <Button variant="outline" onClick={onGoMemos}>
          {tx(i18n, "today_action_default_secondary", "Recent Memos 이동")}
        </Button>
      </CardContent>
    </Card>
  )
}

function SnapshotPreviewCard({ coffees, onOpenSnapshot, i18n }) {
  const preview = coffees.slice(0, 5)
  const locale = tx(i18n, "locale", "ko")

  return (
    <Card className="ui-action-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg">{tx(i18n, "preview_title", "Coffee Snapshot Preview")}</CardTitle>
        <CardDescription>{tx(i18n, "preview_body", "최근 등록 원두 기준으로 브루/평가 흐름을 먼저 확인하세요.")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {preview.length === 0 ? (
          <EmptyState text={tx(i18n, "preview_empty", "원두 데이터가 없습니다.")} />
        ) : (
          <>
            {preview.map((coffee) => (
              <div key={`preview-${coffee.id}`} className="ui-list-row">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{coffee.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{coffee.roaster || tx(i18n, "preview_roaster_fallback", "로스터리 미입력")}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs text-muted-foreground">{tx(i18n, "preview_brews_label", "Brews")} {formatInteger(coffee.total_brews_count, locale)}</p>
                  <p className="text-xs text-muted-foreground">{tx(i18n, "preview_evals_label", "Evals")} {formatInteger(coffee.total_guest_evaluations_count, locale)}</p>
                </div>
              </div>
            ))}
            <Button className={POINT_BUTTON_CLASS} onClick={onOpenSnapshot}>
              {tx(i18n, "preview_open_snapshot", "전체 Snapshot 보기")}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function ChartViewport({ className, children }) {
  const containerRef = React.useRef(null)
  const [ready, setReady] = React.useState(false)

  React.useEffect(() => {
    const node = containerRef.current
    if (!node) return undefined

    const updateReady = () => {
      const nextReady = node.clientWidth > 0 && node.clientHeight > 0
      setReady((current) => (current === nextReady ? current : nextReady))
    }

    const frameId = window.requestAnimationFrame(updateReady)

    if ("ResizeObserver" in window) {
      const observer = new ResizeObserver(updateReady)
      observer.observe(node)
      return () => {
        window.cancelAnimationFrame(frameId)
        observer.disconnect()
      }
    }

    const intervalId = window.setInterval(updateReady, 250)
    return () => {
      window.cancelAnimationFrame(frameId)
      window.clearInterval(intervalId)
    }
  }, [])

  return (
    <div ref={containerRef} className={className}>
      {ready ? (
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      ) : null}
    </div>
  )
}

function toNumber(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatScore(value, locale) {
  if (value == null) return "-"
  const number = Number(value)
  if (!Number.isFinite(number)) return "-"
  return new Intl.NumberFormat(normalizeLocale(locale), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(number)
}

function summarizeProfiles(profiles) {
  const coffeeCount = profiles.length
  const totalEvaluations = profiles.reduce((sum, profile) => sum + toNumber(profile.evaluation_count), 0)
  const weightedRatingSum = profiles.reduce((sum, profile) => {
    return sum + toNumber(profile.average_rating) * toNumber(profile.evaluation_count)
  }, 0)
  const weightedBodySum = profiles.reduce((sum, profile) => {
    return sum + toNumber(profile.average_body) * toNumber(profile.evaluation_count)
  }, 0)

  return {
    coffeeCount,
    totalEvaluations,
    averageRating: totalEvaluations > 0 ? Number((weightedRatingSum / totalEvaluations).toFixed(2)) : null,
    averageBody: totalEvaluations > 0 ? Number((weightedBodySum / totalEvaluations).toFixed(2)) : null,
  }
}

function buildComparisonData(profiles, dimensionKey, metadataFallback = "미입력") {
  const groups = profiles.reduce((acc, profile) => {
    const key = profile[dimensionKey] || metadataFallback
    if (!acc[key]) acc[key] = []
    acc[key].push(profile)
    return acc
  }, {})

  return Object.entries(groups)
    .map(([label, items]) => {
      const summary = summarizeProfiles(items)
      return {
        label,
        coffee_count: summary.coffeeCount,
        evaluations: summary.totalEvaluations,
        average_rating: summary.averageRating,
      }
    })
    .sort((a, b) => b.evaluations - a.evaluations)
    .slice(0, 12)
}

function mergeKeywordRows(rows = [], limit = 10) {
  const merged = new Map()

  rows.forEach((row) => {
    const label = String(row?.label || "Unknown").trim() || "Unknown"
    const count = toNumber(row?.count)
    merged.set(label, (merged.get(label) || 0) + count)
  })

  return Array.from(merged.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

function buildPageItems(currentPage, totalPages, window = 1) {
  if (totalPages <= 1) return []

  const pages = [1, totalPages]
  for (let page = currentPage - window; page <= currentPage + window; page += 1) {
    if (page >= 1 && page <= totalPages) pages.push(page)
  }

  const sortedPages = [...new Set(pages)].sort((a, b) => a - b)
  const items = []

  sortedPages.forEach((page, index) => {
    const previous = sortedPages[index - 1]
    if (previous && page - previous > 1) items.push("ellipsis")
    items.push(page)
  })

  return items
}

function paginateItems(items, page, perPage) {
  const safePerPage = Math.max(1, toNumber(perPage))
  const totalItems = items.length
  const totalPages = Math.max(1, Math.ceil(totalItems / safePerPage))
  const currentPage = Math.min(Math.max(1, toNumber(page)), totalPages)
  const start = (currentPage - 1) * safePerPage
  const pagedItems = items.slice(start, start + safePerPage)

  return {
    items: pagedItems,
    totalItems,
    totalPages,
    currentPage,
  }
}

function SectionPagination({ pagination, onPageChange, i18n }) {
  if (pagination.totalItems === 0 || pagination.totalPages <= 1) return null
  const locale = tx(i18n, "locale", "ko")
  const prevLabel = tx(i18n, "pagination_prev", "Previous")
  const nextLabel = tx(i18n, "pagination_next", "Next")
  const morePagesLabel = tx(i18n, "pagination_more_pages", "More pages")

  const pageItems = buildPageItems(pagination.currentPage, pagination.totalPages)
  const goToPage = (event, page) => {
    event.preventDefault()
    if (page < 1 || page > pagination.totalPages || page === pagination.currentPage) return
    onPageChange(page)
  }

  return (
    <div className="mt-3 space-y-2">
      <p className="text-center text-xs text-muted-foreground">
        {fmt(tx(i18n, "pagination_summary", "페이지 %{page}/%{total} · 총 %{count}개"), {
          page: formatInteger(pagination.currentPage, locale),
          total: formatInteger(pagination.totalPages, locale),
          count: formatInteger(pagination.totalItems, locale),
        })}
      </p>
      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              label={prevLabel}
              ariaLabel={prevLabel}
              onClick={(event) => goToPage(event, pagination.currentPage - 1)}
              className={pagination.currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>

          {pageItems.map((item, index) => (
            <PaginationItem key={`${item}-${index}`}>
              {item === "ellipsis" ? (
                <PaginationEllipsis srLabel={morePagesLabel} />
              ) : (
                <PaginationLink
                  href="#"
                  isActive={item === pagination.currentPage}
                  onClick={(event) => goToPage(event, item)}
                >
                  {item}
                </PaginationLink>
              )}
            </PaginationItem>
          ))}

          <PaginationItem>
            <PaginationNext
              href="#"
              label={nextLabel}
              ariaLabel={nextLabel}
              onClick={(event) => goToPage(event, pagination.currentPage + 1)}
              className={pagination.currentPage >= pagination.totalPages ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
}

export function DashboardInsights({
  inventory_scope = "active",
  summary = {},
  axis_averages = [],
  preference_breakdown = [],
  top_keywords = [],
  recent_memos = [],
  coffees = [],
  meta_explorer = {},
  links = {},
  i18n = {},
}) {
  const locale = tx(i18n, "locale", "ko")
  const chartColors = useChartColors()
  const tooltipStyle = React.useMemo(() => ({
    borderRadius: "6px",
    border: `1px solid ${chartColors.tooltipBorder}`,
    background: chartColors.tooltipBg,
    color: chartColors.tooltipText,
    boxShadow: "none",
  }), [chartColors])
  const normalizedTopKeywords = React.useMemo(() => mergeKeywordRows(top_keywords, 10), [top_keywords])
  const currentInventoryScope = inventory_scope === "all" ? "all" : "active"
  const inventoryScopeLabel = currentInventoryScope === "all"
    ? tx(i18n, "inventory_scope_all", "전체(소진 원두 포함)")
    : tx(i18n, "inventory_scope_active", "사용 중 원두")
  const inventoryScopePaths = links.inventory_scope_paths || {}
  const axisChartData = axis_averages.map((axis) => ({
    ...axis,
    value: Number(axis.value || 0),
  }))

  const hasEvaluationData = Number(summary.total_evaluations || 0) > 0
  const keywordMax = Math.max(...normalizedTopKeywords.map((item) => item.count), 1)
  const sortedCoffees = React.useMemo(() => {
    return [...coffees].sort((a, b) => toNumber(b.created_at_ts) - toNumber(a.created_at_ts))
  }, [coffees])
  const coffeeEvalMax = Math.max(...sortedCoffees.map((item) => item.total_guest_evaluations_count), 1)

  const coffeeProfiles = meta_explorer.coffee_profiles || []
  const distributions = meta_explorer.distributions || {}
  const [metaDimension, setMetaDimension] = React.useState("origin")
  const [selectedMetaValue, setSelectedMetaValue] = React.useState("all")
  const metaOptions = distributions[metaDimension] || []
  const currentMetaDimensionLabel = META_DIMENSIONS.find((item) => item.key === metaDimension)?.label || metaDimension

  React.useEffect(() => {
    setSelectedMetaValue("all")
  }, [metaDimension])

  const filteredProfiles = React.useMemo(() => {
    if (selectedMetaValue === "all") return coffeeProfiles
    return coffeeProfiles.filter((profile) => profile[metaDimension] === selectedMetaValue)
  }, [coffeeProfiles, metaDimension, selectedMetaValue])
  const sortedFilteredProfiles = React.useMemo(() => {
    return [...filteredProfiles].sort((a, b) => toNumber(b.created_at_ts) - toNumber(a.created_at_ts))
  }, [filteredProfiles])

  const filteredSummary = React.useMemo(() => summarizeProfiles(filteredProfiles), [filteredProfiles])
  const comparisonData = React.useMemo(
    () => buildComparisonData(coffeeProfiles, metaDimension, tx(i18n, "metadata_fallback", "미입력")),
    [coffeeProfiles, metaDimension, i18n]
  )
  const comparisonRatingData = comparisonData.map((entry) => ({
    ...entry,
    average_rating_value: toNumber(entry.average_rating),
  }))
  const filteredCoffeeEvalMax = Math.max(...sortedFilteredProfiles.map((profile) => toNumber(profile.evaluation_count)), 1)
  const [analyticsTab, setAnalyticsTab] = React.useState("axis")
  const [coffeeTab, setCoffeeTab] = React.useState("snapshot")
  const [memoPage, setMemoPage] = React.useState(1)
  const [snapshotPage, setSnapshotPage] = React.useState(1)
  const [reactionPage, setReactionPage] = React.useState(1)

  const memoPagination = React.useMemo(
    () => paginateItems(recent_memos, memoPage, SECTION_PAGE_SIZE),
    [recent_memos, memoPage]
  )
  const snapshotPagination = React.useMemo(
    () => paginateItems(sortedCoffees, snapshotPage, SECTION_PAGE_SIZE),
    [sortedCoffees, snapshotPage]
  )
  const reactionPagination = React.useMemo(
    () => paginateItems(sortedFilteredProfiles, reactionPage, SECTION_PAGE_SIZE),
    [sortedFilteredProfiles, reactionPage]
  )

  React.useEffect(() => {
    if (memoPage !== memoPagination.currentPage) setMemoPage(memoPagination.currentPage)
  }, [memoPage, memoPagination.currentPage])

  React.useEffect(() => {
    if (snapshotPage !== snapshotPagination.currentPage) setSnapshotPage(snapshotPagination.currentPage)
  }, [snapshotPage, snapshotPagination.currentPage])

  React.useEffect(() => {
    if (reactionPage !== reactionPagination.currentPage) setReactionPage(reactionPagination.currentPage)
  }, [reactionPage, reactionPagination.currentPage])

  React.useEffect(() => {
    setReactionPage(1)
  }, [metaDimension, selectedMetaValue])

  const focusSectionById = React.useCallback((id) => {
    const section = document.getElementById(id)
    if (!section) return
    const stickyHeaderOffset = 84
    const top = section.getBoundingClientRect().top + window.scrollY - stickyHeaderOffset
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" })
  }, [])

  const focusMemoSection = React.useCallback(() => {
    focusSectionById("recent-memos-section")
  }, [focusSectionById])

  const focusCoffeeSection = React.useCallback(() => {
    focusSectionById("coffee-insights-section")
  }, [focusSectionById])

  return (
    <div className="mx-auto w-full max-w-[1120px] space-y-4 sm:space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{tx(i18n, "title", "Flavor Dashboard")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{tx(i18n, "subtitle", "게스트 평가를 축별 강도, 취향 분포, 키워드 흐름으로 확인합니다.")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{tx(i18n, "criteria_prefix", "기준")}: {inventoryScopeLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            asChild
            size="sm"
            className={currentInventoryScope === "active" ? POINT_BUTTON_CLASS : ""}
            variant={currentInventoryScope === "active" ? "default" : "outline"}
          >
            <a href={inventoryScopePaths.active || "#"}>{tx(i18n, "scope_active_cta", "사용 중 원두")}</a>
          </Button>
          <Button
            asChild
            size="sm"
            className={currentInventoryScope === "all" ? POINT_BUTTON_CLASS : ""}
            variant={currentInventoryScope === "all" ? "default" : "outline"}
          >
            <a href={inventoryScopePaths.all || "#"}>{tx(i18n, "scope_all_cta", "전체")}</a>
          </Button>
          <Badge variant="secondary">{tx(i18n, "evaluations_label", "Evaluations")} {formatInteger(summary.total_evaluations ?? 0, locale)}</Badge>
        </div>
      </div>

      <TodayActionCard
        hasCoffees={sortedCoffees.length > 0}
        hasEvaluations={hasEvaluationData}
        links={links}
        i18n={i18n}
        onGoSnapshot={() => {
          setCoffeeTab("snapshot")
          focusCoffeeSection()
        }}
        onGoMemos={() => {
          focusMemoSection()
        }}
      />

      <section className="grid items-stretch gap-4 md:grid-cols-2">
        <StatCard label="Avg Rating" value={formatScore(summary.average_rating, locale)} hint={tx(i18n, "stat_avg_rating_hint", "전체 평가 기준")} />
        <StatCard label="Evaluations" value={formatInteger(summary.total_evaluations ?? 0, locale)} hint={tx(i18n, "stat_evaluations_hint", "누적 게스트 평가 수")} />
      </section>

      <SnapshotPreviewCard
        coffees={sortedCoffees}
        i18n={i18n}
        onOpenSnapshot={() => {
          setCoffeeTab("snapshot")
          focusCoffeeSection()
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>{tx(i18n, "analytics_title", "Flavor Analytics")}</CardTitle>
          <CardDescription>{tx(i18n, "analytics_body", "축별 강도와 취향 분포를 그래프로 비교하세요.")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={analyticsTab} onValueChange={setAnalyticsTab}>
            <div className="relative">
              <div className="overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <TabsList className="w-max shrink-0 justify-start gap-1 rounded-xl bg-muted/80 p-1">
                  <TabsTrigger value="axis" className="text-xs sm:text-sm">{tx(i18n, "analytics_tab_axis", "Axis Avg")}</TabsTrigger>
                  <TabsTrigger value="preference" className="text-xs sm:text-sm">{tx(i18n, "analytics_tab_preference", "Preference Mix")}</TabsTrigger>
                  <TabsTrigger value="keywords" className="text-xs sm:text-sm">{tx(i18n, "analytics_tab_keywords", "Keyword Trend")}</TabsTrigger>
                </TabsList>
              </div>
              <div className="pointer-events-none absolute inset-y-1 left-0 w-5 bg-gradient-to-r from-background to-transparent sm:hidden" />
              <div className="pointer-events-none absolute inset-y-1 right-0 w-5 bg-gradient-to-l from-background to-transparent sm:hidden" />
            </div>
            <p className="text-[11px] text-muted-foreground sm:hidden">{tx(i18n, "analytics_mobile_hint", "좌우로 밀어 탭 보기")}</p>

            <TabsContent value="axis" className="mt-4">
              {!hasEvaluationData ? (
                <EmptyState text={tx(i18n, "analytics_empty_axis", "평가 데이터가 아직 없습니다.")} />
              ) : analyticsTab === "axis" ? (
                <ChartViewport className="h-[260px] w-full sm:h-[320px]">
                  <BarChart data={axisChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="26%">
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis dataKey="label" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis domain={[0, 5]} tickCount={6} fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: chartColors.cursor }} contentStyle={tooltipStyle} />
                    <Bar dataKey="value" fill={chartColors.mono} radius={[6, 6, 0, 0]} maxBarSize={60} />
                  </BarChart>
                </ChartViewport>
              ) : null}
            </TabsContent>

            <TabsContent value="preference" className="mt-4">
              {!hasEvaluationData ? (
                <EmptyState text={tx(i18n, "analytics_empty_preference", "취향 분포 데이터가 없습니다.")} />
              ) : analyticsTab === "preference" ? (
                <ChartViewport className="h-[260px] w-full sm:h-[320px]">
                  <BarChart data={preference_breakdown} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis dataKey="label" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: chartColors.cursor }} contentStyle={tooltipStyle} />
                    <Bar stackId="pref" dataKey="low" fill={chartColors.light} radius={[4, 4, 0, 0]} />
                    <Bar stackId="pref" dataKey="match" fill={chartColors.soft} radius={[4, 4, 0, 0]} />
                    <Bar stackId="pref" dataKey="high" fill={chartColors.mono} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ChartViewport>
              ) : null}
            </TabsContent>

            <TabsContent value="keywords" className="mt-4">
              {!hasEvaluationData || normalizedTopKeywords.length === 0 ? (
                <EmptyState text={tx(i18n, "analytics_empty_keywords", "키워드 데이터가 없습니다.")} />
              ) : (
                <div className="space-y-3">
                  {normalizedTopKeywords.map((keyword, index) => (
                    <div key={`keyword-${keyword.label}-${keyword.count}-${index}`} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>{keyword.label}</span>
                        <span className="text-muted-foreground">{formatInteger(keyword.count, locale)}</span>
                      </div>
                      <Progress value={(keyword.count / keywordMax) * 100} />
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

          </Tabs>
        </CardContent>
      </Card>

      <section className="grid items-stretch gap-4 md:grid-cols-2">
        {META_DIMENSIONS.map((dimension) => {
          const entries = distributions[dimension.key] || []
          return (
            <Card key={dimension.key} className="h-full">
              <CardHeader>
                <CardTitle>{dimension.label} {tx(i18n, "meta_distribution_suffix", "Distribution")}</CardTitle>
                <CardDescription>{tx(i18n, "meta_distribution_body", "등록된 원두 기준 상위 분포")}</CardDescription>
              </CardHeader>
              <CardContent>
                {entries.length === 0 ? (
                  <EmptyState text={tx(i18n, "meta_distribution_empty", "메타데이터가 없습니다.")} />
                ) : (
                  <div className="space-y-2">
                    {entries.slice(0, 5).map((entry) => (
                      <div key={`${dimension.key}-${entry.value}`} className="flex items-center justify-between text-xs sm:text-sm">
                        <span className="truncate">{entry.value}</span>
                        <Badge variant="outline" className="px-1.5 py-0 text-[10px] sm:px-2.5 sm:text-xs">{formatInteger(entry.count, locale)}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </section>

      <Card>
        <CardHeader>
          <CardTitle>{tx(i18n, "explorer_title", "Coffee Metadata Explorer")}</CardTitle>
          <CardDescription>{tx(i18n, "explorer_body", "원두 메타데이터 필터로 평가 반응을 비교합니다.")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {META_DIMENSIONS.map((dimension) => (
                <Button
                  key={dimension.key}
                  type="button"
                  size="sm"
                  variant={metaDimension === dimension.key ? "default" : "outline"}
                  className="h-7 px-2 text-[11px] sm:h-8 sm:px-3 sm:text-xs"
                  onClick={() => setMetaDimension(dimension.key)}
                >
                  {dimension.label}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={selectedMetaValue === "all" ? "secondary" : "outline"}
              className="h-7 px-2 text-[11px] sm:h-8 sm:px-3 sm:text-xs"
              onClick={() => setSelectedMetaValue("all")}
            >
              {tx(i18n, "explorer_all", "전체")}
            </Button>
            {metaOptions.map((entry) => (
                <Button
                  key={`${metaDimension}-${entry.value}`}
                  type="button"
                  size="sm"
                  variant={selectedMetaValue === entry.value ? "secondary" : "outline"}
                  className="h-7 px-2 text-[11px] sm:h-8 sm:px-3 sm:text-xs"
                  onClick={() => setSelectedMetaValue(entry.value)}
                >
                  {entry.value} ({formatInteger(entry.count, locale)})
              </Button>
            ))}
          </div>

          <section className="grid items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3">
              <StatCard
                label="Filtered Coffees"
                value={formatInteger(filteredSummary.coffeeCount, locale)}
                hint={fmt(tx(i18n, "filtered_coffees_hint", "%{dimension}: %{value}"), { dimension: currentMetaDimensionLabel, value: selectedMetaValue === "all" ? tx(i18n, "explorer_all", "전체") : selectedMetaValue })}
              />
            <StatCard label="Filtered Evaluations" value={formatInteger(filteredSummary.totalEvaluations, locale)} hint={tx(i18n, "filtered_evals_hint", "선택된 원두의 평가 수")} />
            <StatCard label="Avg Rating" value={formatScore(filteredSummary.averageRating, locale)} hint={tx(i18n, "filtered_avg_hint", "가중 평균")} />
          </section>

          <div className="grid items-stretch gap-4">
            <Card className="h-full">
              <CardHeader className="pb-2 sm:pb-6">
                <CardTitle className="text-base sm:text-lg">{tx(i18n, "comparison_title", "Meta Comparison: Rating")}</CardTitle>
                <CardDescription className="text-xs sm:text-sm">{fmt(tx(i18n, "comparison_body", "%{dimension} 그룹 평균 평점"), { dimension: currentMetaDimensionLabel })}</CardDescription>
              </CardHeader>
              <CardContent className="px-2 pb-2 pt-0 sm:px-6 sm:pb-6">
                {comparisonRatingData.length === 0 ? (
                  <EmptyState text={tx(i18n, "comparison_empty", "비교할 데이터가 없습니다.")} />
                ) : (
                  <ChartViewport className="h-[220px] w-full sm:h-[280px]">
                    <BarChart data={comparisonRatingData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={chartColors.grid} />
                      <XAxis dataKey="label" angle={-20} textAnchor="end" interval={0} height={36} fontSize={10} tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 5]} tickLine={false} axisLine={false} fontSize={12} />
                      <Tooltip cursor={{ fill: chartColors.cursor }} contentStyle={tooltipStyle} />
                      <Bar dataKey="average_rating_value" fill={chartColors.mono} radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ChartViewport>
                )}
              </CardContent>
            </Card>
          </div>

        </CardContent>
      </Card>

      <section className="grid items-stretch gap-4 lg:grid-cols-2">
        <Card id="recent-memos-section" className="h-full">
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="text-base sm:text-lg">{tx(i18n, "memos_title", "Recent Memos")}</CardTitle>
            <CardDescription className="text-xs sm:text-sm">{tx(i18n, "memos_body", "게스트 메모를 확인하고 다음 액션을 정리하세요.")}</CardDescription>
          </CardHeader>
          <CardContent>
            {recent_memos.length === 0 ? (
              <EmptyState text={tx(i18n, "memos_empty", "자유 메모가 아직 없습니다.")} />
            ) : (
              <div className="space-y-3">
                {memoPagination.items.map((memo, index) => (
                  <React.Fragment key={memo.id}>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="px-1.5 py-0 text-[10px] sm:px-2.5 sm:text-xs">{tx(i18n, "memos_rating_label", "Rating")} {memo.rating}/5</Badge>
                        <span className="text-[11px] text-muted-foreground sm:text-xs">{memo.created_at}</span>
                      </div>
                      <p className="font-serif-ko text-[13px] leading-6 sm:text-[15px] sm:leading-7">{memo.memo}</p>
                    </div>
                    {index < memoPagination.items.length - 1 ? <Separator /> : null}
                  </React.Fragment>
                ))}
              </div>
            )}
            <SectionPagination pagination={memoPagination} onPageChange={setMemoPage} i18n={i18n} />
          </CardContent>
        </Card>

        <Card id="coffee-insights-section" className="h-full">
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="text-base sm:text-lg">{tx(i18n, "coffee_insights_title", "Coffee Insights")}</CardTitle>
            <CardDescription className="text-xs sm:text-sm">{tx(i18n, "coffee_insights_body", "평가 반응과 브루/평가 현황을 한곳에서 확인합니다.")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={coffeeTab} onValueChange={setCoffeeTab}>
              <div className="relative">
                <div className="overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <TabsList className="w-max shrink-0 justify-start gap-1 rounded-xl bg-muted/80 p-1">
                    <TabsTrigger value="snapshot" className="text-xs sm:text-sm">{tx(i18n, "coffee_tab_snapshot", "Coffee Snapshot")}</TabsTrigger>
                    <TabsTrigger value="reaction" className="text-xs sm:text-sm">{tx(i18n, "coffee_tab_reaction", "Reaction Matrix")}</TabsTrigger>
                  </TabsList>
                </div>
                <div className="pointer-events-none absolute inset-y-1 left-0 w-5 bg-gradient-to-r from-background to-transparent sm:hidden" />
                <div className="pointer-events-none absolute inset-y-1 right-0 w-5 bg-gradient-to-l from-background to-transparent sm:hidden" />
              </div>

              <TabsContent value="reaction" className="mt-3">
                {sortedFilteredProfiles.length === 0 ? (
                  <EmptyState text={tx(i18n, "coffee_insights_empty_filtered", "선택한 조건의 원두가 없습니다.")} />
                ) : (
                  <>
                    <Table className="text-xs sm:text-sm">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="h-8 px-1.5 text-[11px] sm:h-10 sm:px-2">{tx(i18n, "table_coffee", "Coffee")}</TableHead>
                          <TableHead className="h-8 px-1.5 text-[11px] sm:h-10 sm:px-2">{tx(i18n, "table_evals", "Evals")}</TableHead>
                          <TableHead className="h-8 px-1.5 text-[11px] sm:h-10 sm:px-2">{tx(i18n, "table_avg", "Avg")}</TableHead>
                          <TableHead className="hidden h-8 w-[140px] px-1.5 text-[11px] sm:table-cell sm:h-10 sm:px-2">{tx(i18n, "table_momentum", "Momentum")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reactionPagination.items.map((profile) => (
                          <TableRow key={profile.id}>
                            <TableCell className="max-w-[170px] p-1.5 whitespace-normal sm:max-w-none sm:p-2">
                              <a href={profile.show_path} className="block space-y-1 underline-offset-4 hover:underline">
                                <span className="block truncate text-[12px] font-medium sm:text-sm">{profile.name}</span>
                                {profile.roaster ? (
                                  <Badge variant="outline" className="border-border/60 bg-muted/40 px-1.5 py-0 text-[10px] font-normal text-muted-foreground">
                                    {profile.roaster}
                                  </Badge>
                                ) : null}
                              </a>
                            </TableCell>
                            <TableCell className="p-1.5 sm:p-2">{formatInteger(profile.evaluation_count, locale)}</TableCell>
                            <TableCell className="p-1.5 sm:p-2">{formatScore(profile.average_rating, locale)}</TableCell>
                            <TableCell className="hidden p-1.5 sm:table-cell sm:p-2">
                              <Progress value={(toNumber(profile.evaluation_count) / filteredCoffeeEvalMax) * 100} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <SectionPagination pagination={reactionPagination} onPageChange={setReactionPage} i18n={i18n} />
                  </>
                )}
              </TabsContent>

              <TabsContent value="snapshot" className="mt-3">
                {sortedCoffees.length === 0 ? (
                  <EmptyState text={tx(i18n, "coffee_insights_empty_snapshot", "원두 데이터가 없습니다.")} />
                ) : (
                  <>
                    <Table className="table-fixed text-xs sm:text-sm">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="h-8 w-[58%] px-1.5 text-[11px] sm:h-10 sm:w-[44%] sm:px-2">{tx(i18n, "table_coffee", "Coffee")}</TableHead>
                          <TableHead className="h-8 px-1.5 text-[11px] sm:h-10 sm:px-2">{tx(i18n, "table_brews", "Brews")}</TableHead>
                          <TableHead className="h-8 w-[72px] px-1.5 text-[11px] sm:h-10 sm:px-2">{tx(i18n, "table_evals", "Evals")}</TableHead>
                          <TableHead className="hidden h-8 w-[120px] px-1.5 text-[11px] sm:table-cell sm:h-10 sm:px-2">{tx(i18n, "table_momentum", "Momentum")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {snapshotPagination.items.map((coffee) => (
                          <TableRow key={coffee.id}>
                            <TableCell className="max-w-[170px] p-1.5 whitespace-normal sm:max-w-none sm:p-2">
                              <a href={coffee.show_path} className="block space-y-1 underline-offset-4 hover:underline">
                                <span className="block truncate text-[12px] font-medium sm:text-sm">{coffee.name}</span>
                                {coffee.roaster ? (
                                  <Badge variant="outline" className="border-border/60 bg-muted/40 px-1.5 py-0 text-[10px] font-normal text-muted-foreground">
                                    {coffee.roaster}
                                  </Badge>
                                ) : null}
                              </a>
                            </TableCell>
                            <TableCell className="p-1.5 sm:p-2">{formatInteger(coffee.total_brews_count, locale)}</TableCell>
                            <TableCell className="p-1.5 sm:p-2">{formatInteger(coffee.total_guest_evaluations_count, locale)}</TableCell>
                            <TableCell className="hidden p-1.5 sm:table-cell sm:p-2">
                              <Progress value={(coffee.total_guest_evaluations_count / coffeeEvalMax) * 100} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <SectionPagination pagination={snapshotPagination} onPageChange={setSnapshotPage} i18n={i18n} />
                  </>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}
